/**
 * Browse: the finder of the Sentencing Research workspace — the filter-first complement to
 * Ask mode, mirroring the document inventory's interaction grammar and the legacy app's
 * drill-down richness.
 *
 * One pool of sentences (owner, 2026-10-10: "a sentence is a sentence"): DOC's records and
 * the sentences Montana Supreme Court opinions and Sentence Review decisions report that DOC
 * does not hold, in ONE list, newest sentencing first. A DOC sentencing an opinion also
 * reports is one row, known from both. Each row says where it is known from and links to
 * its opinion when it has one.
 *
 *   - the inventory's chip rail (components/inventory/FilterRail): [+ Add filter] opens a
 *     type-ahead picker over every axis — statute, county, judge, year, sentence type,
 *     commitment, suspension, sex, race, DOC's modifiers, on appeal, document, known from —
 *     with CASCADING counts (each axis's options are computed with its own filter excluded,
 *     so picking Yellowstone still shows the other counties to pivot to); one row of boxes
 *     for the name, the age, the term and a boolean search of the facts
 *   - a filter a row cannot answer leaves it out, and the count line says how many
 *   - one statistic over the result, the mix by source under it, two lenses one click away
 *   - a paged table; a column header sorts EVERYTHING the filters match, on the server (the
 *     owner, 2026-10-10: "clicking any column header should sort ALL the currently filtered
 *     data by that column"); a row with facts shows the matching words under it
 *   - row click → the record page: every charge on the docket, what DOC published before,
 *     what the opinion says (sentence, facts, holdings), the offender information
 *   - Download CSV of the full filtered set
 *
 * Data: POST /sentencing/records, /records/facets, /records/summary, /records/export — the
 * same query layer the Arc tools use, no LLM in the loop. Opinion sections: SentencingOpinion.jsx.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import {
  FATE_SCOPE_NOTES, KNOWN_FROM_LABELS, MODIFIER_LABELS, SECTION_FROM_NAME_NOTE,
  afterAppealLabel, countOf, describeFilters, docChangeLabel, docChangedNote, docNotListedNote, exportDate, formatTerm, knownFromOf,
  leftOutLines, monthsShort, parseBrowseParams, shortCite, yearsNote,
} from '../domain/sentencing';
import {
  ADD_FILTER_HINT, SENTENCING_AXES, browseStateFromFilters, chipsToFilters, getSentencingPickerRows,
} from '../domain/sentencingFilterAxes';
import FilterRail from './inventory/FilterRail';
import { OpinionDetail, OpinionLinks } from './SentencingOpinion';
import { FactSnippets, KnownFromBadge, LensedStatistic } from './SentencingCards';
import './SentencingCards.css';
import './SentencingBrowse.css';

const PAGE_SIZE = 50;

// `sort` = the API's sort_by for the column; `width` = the column's share of the table
// (table-layout: fixed, so long values wrap in their column instead of widening the table);
// `dirs` = the two directions in words, for the header's tooltip.
const AZ = ['A to Z', 'Z to A'];
const LOW_HIGH = ['lowest first', 'highest first'];
const COLUMNS = [
  { key: 'defendant', label: 'Defendant', sort: 'defendant', width: 11, dirs: AZ },
  { key: 'docket', label: 'Docket', sort: 'docket', width: 8, dirs: AZ },
  { key: 'county', label: 'County', sort: 'county', width: 8, dirs: AZ },
  { key: 'judge', label: 'Judge', sort: 'judge', width: 7, dirs: AZ },
  { key: 'mca_section', label: 'MCA §', sort: 'mca_section', width: 8, dirs: LOW_HIGH },
  { key: 'description', label: 'Offense', sort: 'description', width: 14, dirs: AZ },
  { key: 'term', label: 'Term', sort: 'term', width: 7, dirs: ['shortest first', 'longest first'] },
  { key: 'total_term', label: 'Total term', sort: 'total_term', width: 8, dirs: ['shortest first', 'longest first'] },
  { key: 'imposed_on', label: 'Imposed', sort: 'imposed_on', width: 8, dirs: ['oldest first', 'newest first'] },
  { key: 'age_at_sentencing', label: 'Age', sort: 'age', width: 4, dirs: ['youngest first', 'oldest first'] },
  { key: 'after_appeal', label: 'On appeal', sort: 'after_appeal', width: 8, dirs: ['stands first', 'reversed first'] },
  { key: 'known_from', label: 'Known from', sort: 'known_from', width: 9, dirs: ['DOC first', 'opinion first'] },
];
const SORT_KEYS = new Set(COLUMNS.map((c) => c.sort));

const DEMOGRAPHIC_FIELDS = [
  ['sex', 'Sex'], ['race', 'Race'], ['birth_year', 'Birth year'],
  ['birth_state', 'Birth state'], ['citizenship', 'Citizenship'],
  ['height_in', 'Height (in)'], ['weight_lb', 'Weight (lb)'],
  ['handedness', 'Handedness'], ['hair_color', 'Hair'], ['build', 'Build'],
  ['skin_tone', 'Skin tone'], ['eye_color', 'Eyes'],
  ['correctional_status', 'Correctional status'], ['status_start_date', 'Status start'],
  ['location_name', 'Facility'], ['location_address', 'Facility address'],
  ['location_phone', 'Facility phone'], ['montana_resident', 'MT resident'],
  ['projected_doc_expiration', 'Projected DOC expiration'],
];

function fateText(rec) {
  if (rec.after_appeal) return afterAppealLabel(rec.after_appeal);
  const set = rec.after_appeal_set || [];
  return set.length ? set.map(afterAppealLabel).join(', ') : '—';
}

function formatCell(rec, key) {
  if (key === 'known_from') return KNOWN_FROM_LABELS[knownFromOf(rec)] || '—';
  if (key === 'after_appeal') return fateText(rec);
  const v = rec[key];
  if (v == null || v === '') return '—';
  if ((key === 'term' || key === 'total_term') && typeof v === 'object') {
    const value = v.value ?? v.length;
    return value == null ? '—' : `${value} ${v.unit || ''}`.trim();
  }
  return String(v);
}

// MCA title → hue, so charge families read at a glance (45 = offenses,
// 61 = vehicles/DUI, 46 = procedure/enhancements, 50 = drugs/health).
const MCA_TITLE_CLASS = {
  45: 'crimes',
  46: 'procedure',
  61: 'vehicles',
  50: 'health',
  87: 'wildlife',
};

// A compound section ("45-4-103;45-5-102": the attempt statute and the offense) is one badge
// per section, wrapping in the cell, so a long code never runs into the next column.
function McaBadge({ code, fromName = false }) {
  if (!code) return '—';
  const sections = String(code).split(';').map((s) => s.trim()).filter(Boolean);
  const badge = (section) => {
    const title = section.split('-')[0];
    const cls = MCA_TITLE_CLASS[title] || 'other';
    return (
      <span key={section} className={`sb-mca sb-mca-${cls}${fromName ? ' sb-mca-from-name' : ''}`} title={fromName ? SECTION_FROM_NAME_NOTE : undefined}>
        {section}
      </span>
    );
  };
  if (sections.length <= 1) return badge(sections[0] || String(code));
  return <span className="sb-mca-group">{sections.map(badge)}</span>;
}

function termMonths(v) {
  if (v == null) return null;
  if (typeof v === 'object') {
    const val = v.value ?? v.length;
    if (typeof val !== 'number') return null;
    return String(v.unit || '').toLowerCase().startsWith('year') ? val * 12 : val;
  }
  const m = String(v).match(/^(\d+(?:\.\d+)?)\s*month/i);
  if (m) return Number(m[1]);
  const y = String(v).match(/^(\d+(?:\.\d+)?)\s*year/i);
  if (y) return Number(y[1]) * 12;
  return null;
}

// The term as a pill, in months ("36 mo"), the years in its tooltip; the shade follows the length.
function TermPill({ rec, field }) {
  const text = formatCell(rec, field);
  if (text === '—') return '—';
  const lower = text.toLowerCase();
  const months = termMonths(rec[field]);
  let sev = 'na';
  if (lower.includes('life') || lower.includes('death')) sev = 'max';
  else if (months != null) {
    sev = months >= 240 ? 'max' : months >= 120 ? 'high' : months >= 36 ? 'mid' : 'low';
  }
  const years = months != null ? yearsNote(months) : '';
  return <span className={`sb-term sb-term-${sev}`} title={years ? `${text} (${years})` : text}>{months != null ? monthsShort(months) : text}</span>;
}

// The row is DOC's latest. The mark says that DOC changed the sentence in a later export; the
// record page has what DOC published before. (That DOC's current export no longer lists an
// order is said on the record page only: one row in eight would carry that mark.)
function DocHistoryMark({ history }) {
  if (!history?.changed) return null;
  return <span className="sb-doc-mark sb-doc-changed" title={docChangedNote(history)}>changed</span>;
}

// What DOC published before for one sentencing, oldest first, each version with the export
// that replaced it and what that export changed; and whether DOC still lists the sentencing.
function DocHistory({ imposedOn, history }) {
  if (!history || !(history.changed || history.no_longer_listed)) return null;
  return (
    <div className="sb-doc-history">
      {history.no_longer_listed && <div className="sb-doc-history-note">{docNotListedNote(history, imposedOn)}</div>}
      {history.changed && (
        <>
          <div className="sb-detail-label">
            What DOC published before (changed in the export of {exportDate(history.changed_in)})
          </div>
          {history.earlier_versions.map((v) => (
            <div key={v.first_published} className="sb-doc-version">
              <div className="sb-doc-version-head">
                <span className="sb-cell-mono">{exportDate(v.first_published)} – {exportDate(v.replaced_in)}</span>
                {v.sentence_type && <span>{v.sentence_type}</span>}
                <span>Total term: {formatTerm(v.total_term)}</span>
                {v.commit_type && <span>{v.commit_type}{v.suspension ? `, ${v.suspension}` : ''}</span>}
                {v.judge && <span className="sb-cell-muted">Judge {v.judge}</span>}
              </div>
              {(v.offenses || []).map((o, i) => (
                <div key={`${o.offense}-${i}`} className="sb-detail-charge">
                  <McaBadge code={o.mca_section} />
                  <span className="sb-detail-charge-desc">{o.offense || '—'}{o.counts > 1 ? ` ×${o.counts}` : ''}</span>
                  <span>{formatTerm(o.term)}</span>
                  <span className="sb-popover-n">{o.offense_date ? `offense ${o.offense_date}` : ''}</span>
                </div>
              ))}
              <div className="sb-doc-version-changed">
                The export of {exportDate(v.replaced_in)} changed: {(v.what_changed || []).map(docChangeLabel).join(', ') || 'details'}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function renderCell(rec, key, expanded) {
  const fromOpinion = knownFromOf(rec) === 'opinion';
  switch (key) {
    case 'defendant':
      return (
        <span className="sb-cell-defendant">
          <span className={`sb-caret${expanded ? ' sb-caret-open' : ''}`}>▸</span>
          {formatCell(rec, key)}
        </span>
      );
    case 'mca_section':
      return <McaBadge code={rec.mca_section} fromName={Boolean(rec.mca_from_name)} />;
    case 'description':
      if (rec.description_withheld) {
        return (
          <span className="so-muted" title="The offense label is shown once this opinion has passed its privacy check">
            {rec.is_total ? 'Total' : 'label withheld'}
          </span>
        );
      }
      return formatCell(rec, key);
    case 'after_appeal': {
      // one chip per fate, so a row with several wraps instead of widening the column; the
      // tooltip says whether the fate is this count's or the case's across its counts
      const fates = rec.after_appeal ? [rec.after_appeal] : (rec.after_appeal_set || []);
      if (!fates.length) return <span className="sb-cell-na" title="No opinion on this case: what an appeal did is known only from an opinion">—</span>;
      return (
        <span className="sb-fates" title={FATE_SCOPE_NOTES[rec.fate_scope] || FATE_SCOPE_NOTES.count}>
          {fates.map((f) => <span key={f} className={`so-fate so-fate-${f}`}>{afterAppealLabel(f)}</span>)}
        </span>
      );
    }
    case 'known_from': {
      const opinions = rec.opinions && rec.opinions.length ? rec.opinions : (rec.opinion ? [rec.opinion] : []);
      return (
        <span className="sb-known-cell">
          <KnownFromBadge rec={rec} />
          {opinions.map((o) => <OpinionLinks key={o.opinion_id} opinion={{ ...o, cite: shortCite(o) }} compact />)}
        </span>
      );
    }
    case 'age_at_sentencing':
      if (rec.age_at_sentencing == null) {
        return <span className="sb-cell-na" title={fromOpinion ? 'The opinion does not state an age' : 'DOC gives no birth year or date for this row'}>—</span>;
      }
      return String(rec.age_at_sentencing);
    case 'term':
      return <TermPill rec={rec} field={key} />;
    case 'total_term':
      return (
        <>
          <TermPill rec={rec} field={key} />
          <DocHistoryMark history={rec.doc_history} />
        </>
      );
    case 'docket':
    case 'imposed_on':
      return <span className="sb-cell-mono">{formatCell(rec, key)}</span>;
    case 'county':
    case 'judge': {
      const text = formatCell(rec, key);
      if (text === '—' && fromOpinion) return <span className="sb-cell-na" title={`The opinion does not state the ${key}`}>—</span>;
      return <span className="sb-cell-muted">{text}</span>;
    }
    default:
      return formatCell(rec, key);
  }
}

/** A from–to pair of boxes, applied on Enter or on leaving the box. */
function RangeBoxes({ label, title, value, onChange, onApply, width }) {
  return (
    <form
      className="sb-range"
      title={title}
      onSubmit={(e) => { e.preventDefault(); onApply(); }}
      onBlur={onApply}
    >
      <span className="sb-range-label">{label}</span>
      <input
        className="sb-range-input" inputMode="numeric" placeholder="from" aria-label={`${label} from`} style={width ? { width } : undefined}
        value={value.min} onChange={(e) => onChange({ ...value, min: e.target.value.replace(/\D/g, '') })}
      />
      <span>–</span>
      <input
        className="sb-range-input" inputMode="numeric" placeholder="to" aria-label={`${label} to`} style={width ? { width } : undefined}
        value={value.max} onChange={(e) => onChange({ ...value, max: e.target.value.replace(/\D/g, '') })}
      />
      <button type="submit" hidden />
    </form>
  );
}

/**
 * The record page of a DOC row: the whole case on this docket (every sentencing, newest
 * first, with its charges, total and DOC history), what the opinion says when the case has
 * one, and the offender information DOC gives.
 */
function DocketDetail({ docket, county, docId, opinions }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.post('/sentencing/records', {
      // This person's case on this docket — the WHOLE case, not just the counts that matched
      // the rail filters. A docket number alone is not a case: "DC-06-55" exists in several
      // counties, and the drill-down used to list another person's charges with it.
      filters: {
        dockets: [docket],
        known_from: ['doc', 'both'],
        ...(county ? { counties: [county] } : {}),
        ...(docId ? { doc_ids: [docId] } : {}),
      },
      limit: 50,
      include_details: true,
    })
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load'); });
    return () => { cancelled = true; };
  }, [docket, county, docId]);

  if (error) return <div className="sb-detail sb-error">{error}</div>;
  if (!detail) return <div className="sb-detail sb-detail-loading">Loading case…</div>;

  const records = detail.records || [];
  if (records.length === 0) return <div className="sb-detail">No detail available.</div>;
  const head = records[0];
  // A docket can hold several sentencings (the original sentence, a revocation years later):
  // each is shown on its own, newest first, with its own charges, total and DOC history.
  const sentencings = [];
  for (const r of records) {
    const key = r.imposed_on || 'undated';
    let group = sentencings.find((g) => g.key === key);
    if (!group) { group = { key, head: r, charges: [] }; sentencings.push(group); }
    group.charges.push(r);
  }

  return (
    <div className="sb-detail">
      <div className="sb-detail-case">
        <span><strong>{head.defendant || 'Unknown defendant'}</strong></span>
        <span>Docket {head.docket || '—'}</span>
        <span>{head.county || '—'} County</span>
        {sentencings.length > 1 && <span>{countOf(sentencings.length, 'sentencing')} on this docket</span>}
        <KnownFromBadge rec={{ known_from: (opinions || []).length ? 'both' : 'doc' }} />
      </div>

      {sentencings.map((sg) => (
        <div key={sg.key} className="sb-detail-charges sb-detail-sentencing">
          <div className="sb-detail-sentencing-head">
            <span>Sentence imposed {formatCell(sg.head, 'imposed_on')}</span>
            {sg.head.procedural_sentence_type && <span>{sg.head.procedural_sentence_type}</span>}
            <span>Judge {sg.head.judge || '—'}</span>
            <DocHistoryMark history={sg.head.doc_history} />
          </div>
          {sg.charges.map((c, i) => (
            <div key={`chg-${i}`} className="sb-detail-charge">
              <McaBadge code={c.mca_section || c.offense_code} />
              <span className="sb-detail-charge-desc">
                {c.description || '—'}
                {(c.modifiers || []).map((m) => <span key={m} className="so-tag">{MODIFIER_LABELS[m] || m}</span>)}
              </span>
              <span>
                <TermPill rec={c} field="term" />
                {c.commit_type ? `, ${c.commit_type}` : ''}
                {c.suspension ? `, ${c.suspension}` : ''}
              </span>
              <span className="sb-popover-n">{c.offense_date ? `offense ${c.offense_date}` : ''}</span>
            </div>
          ))}
          {sg.head.total_term && (
            <div className="sb-detail-total">Total term: {formatCell(sg.head, 'total_term')}</div>
          )}
          <DocHistory imposedOn={sg.head.imposed_on} history={sg.head.doc_history} />
        </div>
      ))}

      {(opinions || []).length > 0 && (
        <div className="so-doc-opinions">
          <div className="sb-detail-label">What the opinion says about this case</div>
          {opinions.map((o) => (
            <OpinionDetail key={o.opinion_id} opinionId={o.opinion_id} showDocSide={false} />
          ))}
          <div className="so-muted so-sides-note">
            The opinion reports the sentence as imposed; DOC, above, the sentence now in force (after any
            amendment, revocation or sentence review). Neither corrects the other. Check the judgment.
          </div>
        </div>
      )}

      <div className="sb-detail-label">Offender information</div>
      <div className="sb-detail-demo">
        {DEMOGRAPHIC_FIELDS.filter(([k]) => head[k] != null && head[k] !== '').map(([k, label]) => (
          <div key={k} className="sb-detail-demo-item">
            <span className="sb-detail-demo-key">{label}</span>
            <span>{String(head[k])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** One record page for every row: a DOC row opens its case, a row known from an opinion the opinion. */
function RecordDetail({ rec }) {
  if (knownFromOf(rec) === 'opinion') {
    return <OpinionDetail opinionId={rec.opinion?.opinion_id} />;
  }
  return <DocketDetail docket={rec.docket} county={rec.county} docId={rec.doc_id} opinions={rec.opinions} />;
}

// Filters arriving in the URL (a chat card's "Open in Browse") — read once. Every filter the
// link carries is kept: as a chip, in a box, or verbatim in `extra` (shown as a removable
// chip), so Browse opens on the SAME set the card was computed over (owner, 2026-10-11).
function initialState() {
  const { filters } = parseBrowseParams(typeof window !== 'undefined' ? window.location.search : '');
  return browseStateFromFilters(filters, SORT_KEYS);
}

const FACTS_HELP = 'AND · OR · NOT or -word · ( ) · "exact phrase" · prefix* — words side by side are AND. Reaches the sentences that have an opinion.';

export default function SentencingBrowse({ opinionsLoaded = false }) {
  const initial = useMemo(initialState, []);
  // The chips: [{axis, value}], AND across axes, OR within an axis (the inventory's model).
  const [chips, setChips] = useState(initial.chips);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [nameQuery, setNameQuery] = useState(initial.name);
  const [appliedName, setAppliedName] = useState(initial.name);
  // Boxes are typed in, applied on Enter / leaving the box.
  const [ageInput, setAgeInput] = useState(initial.age);
  const [appliedAge, setAppliedAge] = useState(initial.age);
  const [termInput, setTermInput] = useState(initial.term);
  const [appliedTerm, setAppliedTerm] = useState(initial.term);
  const [yearsInput, setYearsInput] = useState(initial.years);
  const [appliedYears, setAppliedYears] = useState(initial.years);
  const [factsInput, setFactsInput] = useState(initial.facts);
  const [appliedFacts, setAppliedFacts] = useState(initial.facts);
  // Filters from a link that no chip or box can show (a total-term range, an offense label,
  // a docket…): sent as they came, shown as chips, dropped one by one or with Clear all.
  const [extraFilters, setExtraFilters] = useState(initial.extra);
  const [facets, setFacets] = useState({});
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [bySource, setBySource] = useState({});
  const [leftOut, setLeftOut] = useState({});
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // The order of the whole result: {by, dir} from a column header, or null = newest first.
  const [sort, setSort] = useState(initial.sort);
  const [expandedKey, setExpandedKey] = useState(null);
  const [exporting, setExporting] = useState(false);
  const loadSeq = useRef(0);
  const columns = opinionsLoaded ? COLUMNS : COLUMNS.filter((c) => c.key !== 'after_appeal' && c.key !== 'known_from');
  const widthTotal = columns.reduce((sum, c) => sum + c.width, 0);
  const sortBody = sort ? { sort_by: sort.by, sort_dir: sort.dir } : {};

  // Chips that need the opinion tables are dropped where they are not loaded.
  const liveChips = useMemo(
    () => chips.filter((c) => opinionsLoaded || !SENTENCING_AXES[c.axis]?.opinions),
    [chips, opinionsLoaded],
  );

  const buildFilters = useCallback(() => {
    const f = { ...extraFilters, ...chipsToFilters(liveChips) };
    if (appliedName) f.last_names = [appliedName];
    const ylo = parseInt(appliedYears.min, 10);
    const yhi = parseInt(appliedYears.max, 10);
    if (Number.isInteger(ylo)) f.sentencing_year_min = ylo;
    if (Number.isInteger(yhi)) f.sentencing_year_max = yhi;
    const lo = parseInt(appliedAge.min, 10);
    const hi = parseInt(appliedAge.max, 10);
    if (Number.isInteger(lo)) f.age_at_sentencing_min = lo;
    if (Number.isInteger(hi)) f.age_at_sentencing_max = hi;
    const tlo = parseInt(appliedTerm.min, 10);
    const thi = parseInt(appliedTerm.max, 10);
    if (Number.isInteger(tlo)) f.term_months_min = tlo;
    if (Number.isInteger(thi)) f.term_months_max = thi;
    if (opinionsLoaded && appliedFacts.query.trim()) {
      f.facts_query = appliedFacts.query.trim();
      if (appliedFacts.scope === 'opinion') f.facts_scope = 'opinion';
    }
    return f;
  }, [liveChips, extraFilters, appliedName, appliedAge, appliedTerm, appliedYears, appliedFacts, opinionsLoaded]);

  const load = useCallback(async (newOffset = 0) => {
    setLoading(true);
    setError(null);
    setExpandedKey(null);
    // A slower answer to an earlier question (an older filter) must not land on top of the
    // current one.
    const seq = ++loadSeq.current;
    try {
      const filters = buildFilters();
      const [recData, facetData] = await Promise.all([
        api.post('/sentencing/records', { filters, limit: PAGE_SIZE, offset: newOffset, ...sortBody }),
        api.post('/sentencing/records/facets', { filters }),
      ]);
      if (seq !== loadSeq.current) return;
      setRows(recData.records || []);
      setTotal(recData.total_matching ?? null);
      setBySource(recData.by_source || {});
      setLeftOut(recData.left_out || {});
      setOffset(newOffset);
      setFacets(facetData || {});
    } catch (e) {
      if (seq === loadSeq.current) setError(e.message || 'Query failed');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [buildFilters, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new filter set or a new order starts at page 1.
  useEffect(() => { load(0); }, [load]);

  // One statistic per filter set, over the same rows as the table. While its query runs the
  // box dims, so the previous figure is never read as the new one. A failure only hides it.
  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    api.post('/sentencing/records/summary', { filters: buildFilters() })
      .then((d) => { if (!cancelled) setSummary(d); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [buildFilters]);

  const addChip = ({ axis, value }) => setChips((prev) => (
    prev.some((c) => c.axis === axis && c.value === value) ? prev : [...prev, { axis, value }]
  ));
  const removeChip = (axis, value) => setChips((prev) => {
    const next = prev.filter((c) => !(c.axis === axis && c.value === value));
    // Dropping the last primary statute makes the 2nd-charge chips moot.
    return next.some((c) => c.axis === 'mca') ? next : next.filter((c) => c.axis !== 'mca2');
  });

  const clearAll = () => {
    setChips([]); setExtraFilters({}); setAppliedName(''); setNameQuery('');
    setAgeInput({ min: '', max: '' }); setAppliedAge({ min: '', max: '' });
    setTermInput({ min: '', max: '' }); setAppliedTerm({ min: '', max: '' });
    setYearsInput({ min: '', max: '' }); setAppliedYears({ min: '', max: '' });
    setFactsInput({ query: '', scope: 'facts' }); setAppliedFacts({ query: '', scope: 'facts' });
  };
  const dropExtra = (key) => setExtraFilters((prev) => {
    const next = { ...prev };
    delete next[key];
    if (key === 'facts_query') delete next.facts_scope;
    return next;
  });
  const extraChips = Object.keys(extraFilters).filter((k) => k !== 'facts_scope').map((k) => ({
    key: k, text: describeFilters({ [k]: extraFilters[k], ...(k === 'facts_query' ? { facts_scope: extraFilters.facts_scope } : {}) })[0] || k,
  }));

  // What the picker reads: the server's facets (each already without its own filter).
  const pickerCtx = useMemo(() => ({ facets, opinionsLoaded, chips: liveChips }), [facets, opinionsLoaded, liveChips]);

  // The statistic's lens IS the "Known from" chips: "Appealed cases only" = every sentence
  // with an opinion (known from DOC and an opinion, or from an opinion only); "DOC only" =
  // every sentence DOC holds (known from DOC, or from DOC and an opinion). Clicking a lens
  // sets the chips, so the list, the count line and the figures always agree.
  const lens = useMemo(() => {
    const known = new Set(liveChips.filter((c) => c.axis === 'known').map((c) => c.value));
    if (known.size === 2 && known.has('both') && known.has('opinion')) return 'appealed';
    if (known.size === 2 && known.has('doc') && known.has('both')) return 'doc';
    return 'all';
  }, [liveChips]);
  const setLens = (key) => setChips((prev) => {
    const rest = prev.filter((c) => c.axis !== 'known');
    if (key === 'appealed') return [...rest, { axis: 'known', value: 'both' }, { axis: 'known', value: 'opinion' }];
    if (key === 'doc') return [...rest, { axis: 'known', value: 'doc' }, { axis: 'known', value: 'both' }];
    return rest;
  });
  // A column header sorts everything the filters match, on the server: one click one way,
  // a second the other, a third back to newest sentencing first.
  const onHeader = (c) => {
    setSort((prev) => {
      if (prev?.by !== c.sort) return { by: c.sort, dir: 'asc' };
      return prev.dir === 'asc' ? { by: c.sort, dir: 'desc' } : null;
    });
  };
  const headerTitle = (c) => {
    if (sort?.by !== c.sort) return `Sort everything the filters match by ${c.label.toLowerCase()}, ${c.dirs[0]}`;
    return sort.dir === 'asc'
      ? `Sorted by ${c.label.toLowerCase()}, ${c.dirs[0]} — click for ${c.dirs[1]}`
      : `Sorted by ${c.label.toLowerCase()}, ${c.dirs[1]} — click to return to newest sentencing first`;
  };

  const downloadCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await api.raw('POST', '/sentencing/records/export', { filters: buildFilters(), ...sortBody });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sentencing_records.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const anyFilter = liveChips.length > 0 || extraChips.length > 0 || appliedName || appliedAge.min || appliedAge.max
    || appliedTerm.min || appliedTerm.max || appliedYears.min || appliedYears.max || appliedFacts.query.trim();

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = total != null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;
  const mixText = opinionsLoaded && total
    ? [bySource.doc != null && `${Number(bySource.doc).toLocaleString()} from DOC`,
       bySource.opinion != null && `${Number(bySource.opinion).toLocaleString()} from an opinion only`].filter(Boolean).join(' · ')
    : '';
  const leftOutText = leftOutLines(leftOut);

  return (
    <div className="sb-root">
      <div className="sb-rail">
        <FilterRail
          filters={liveChips}
          documents={[]}
          ctx={pickerCtx}
          axes={SENTENCING_AXES}
          getRows={getSentencingPickerRows}
          addHint={ADD_FILTER_HINT}
          onAdd={addChip}
          onRemove={removeChip}
          onClearAll={() => setChips([])}
        />
        <div className="sb-rail-inputs">
          <form
            className="sb-name-search"
            onSubmit={(e) => { e.preventDefault(); setAppliedName(nameQuery.trim()); }}
          >
            <input
              className="sb-name-input"
              placeholder="Defendant last name…"
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
            />
          </form>
          <RangeBoxes
            label="Age at sentencing"
            title="Age when sentenced: the sentencing year minus the birth year (DOC gives the birth year only, so it can be one year high). Sentences known from an opinion carry no age and are left out."
            value={ageInput} onChange={setAgeInput} onApply={() => setAppliedAge({ ...ageInput })}
          />
          <RangeBoxes
            label="Term (months)" title="The term of the count, in months"
            value={termInput} onChange={setTermInput} onApply={() => setAppliedTerm({ ...termInput })} width={56}
          />
          <RangeBoxes
            label="Sentenced (year)" title="The year the sentence was pronounced, from–to (an opinion that gives only a year counts too)"
            value={yearsInput} onChange={setYearsInput} onApply={() => setAppliedYears({ ...yearsInput })} width={52}
          />
          {opinionsLoaded && (
            <form
              className="sb-facts-form"
              onSubmit={(e) => { e.preventDefault(); setAppliedFacts({ ...factsInput, query: factsInput.query.trim() }); }}
              title={FACTS_HELP}
            >
              <input
                className="sb-facts-input"
                placeholder='Facts contain… e.g. vehicle AND (alcohol OR drunk)'
                value={factsInput.query}
                onChange={(e) => setFactsInput((p) => ({ ...p, query: e.target.value }))}
                aria-label="Boolean search of the facts the Court recites"
              />
              <label className="sb-toggle" title="Search every paragraph of the majority opinion, for words the facts summary may leave out">
                <input
                  type="checkbox"
                  checked={factsInput.scope === 'opinion'}
                  onChange={(e) => setFactsInput((p) => ({ ...p, scope: e.target.checked ? 'opinion' : 'facts' }))}
                />
                whole opinion
              </label>
              <button type="submit" className="btn btn-sm btn-ghost" disabled={loading}>Search</button>
            </form>
          )}
          {anyFilter && <button className="sb-clear-all" onClick={clearAll}>Clear all</button>}
          <span className="sb-rail-spacer" />
          <button
            className="btn btn-sm btn-ghost"
            onClick={downloadCsv}
            disabled={exporting || loading || total === 0}
            title="Download all matching sentences (up to 5,000) as CSV"
          >
            {exporting ? 'Exporting…' : '⬇ CSV'}
          </button>
        </div>
      </div>

      {(appliedName || appliedFacts.query.trim() || extraChips.length > 0) && (
        <div className="sb-active-chips">
          {extraChips.map((c) => (
            <span key={c.key} className="sb-active-chip sb-active-chip-extra" title="A filter from the link that opened this view; it has no chip of its own here and is applied as it came">
              {c.text}
              <button onClick={() => dropExtra(c.key)} aria-label="Remove filter">×</button>
            </span>
          ))}
          {appliedName && (
            <span className="sb-active-chip">
              Name: {appliedName}
              <button onClick={() => { setAppliedName(''); setNameQuery(''); }} aria-label="Remove filter">×</button>
            </span>
          )}
          {appliedFacts.query.trim() && (
            <span className="sb-active-chip">
              Facts: {appliedFacts.query.trim()}{appliedFacts.scope === 'opinion' ? ' (whole opinion)' : ''}
              <button onClick={() => { setAppliedFacts({ query: '', scope: 'facts' }); setFactsInput({ query: '', scope: 'facts' }); }} aria-label="Remove filter">×</button>
            </span>
          )}
        </div>
      )}

      <div className={summaryLoading ? 'sb-stat-updating' : undefined}>
        <LensedStatistic summary={summary} lens={lens} onLens={setLens} applied />
      </div>

      <div className="sb-meta">
        {loading
          ? 'Loading…'
          : total != null
            ? (
              <>
                <strong className="sb-meta-count">{total.toLocaleString()}</strong>
                {' matching sentences'}
                {mixText && ` · ${mixText}`}
                {' — click a row for the whole record'}
                {rows.some((r) => r.mca_from_name) && (
                  <span className="so-muted sb-meta-legend">
                    <span className="sb-mca sb-mca-other sb-mca-from-name">§</span> dashed: the opinion cites no section; DOC's section for the offense name
                  </span>
                )}
                {leftOutText.map((line) => <span key={line} className="sb-meta-leftout">{line}</span>)}
              </>
            )
            : ''}
        {error && <span className="sb-error"> {error}</span>}
      </div>

      <div className="sentencing-table-wrap sb-table-wrap">
        <table className="sentencing-table sb-table">
          <colgroup>
            {columns.map((c) => <col key={c.key} style={{ width: `${((c.width / widthTotal) * 100).toFixed(2)}%` }} />)}
          </colgroup>
          <thead>
            <tr>
              {columns.map((c) => {
                const sorted = sort?.by === c.sort;
                return (
                  <th
                    key={c.key}
                    onClick={() => onHeader(c)}
                    title={headerTitle(c)}
                    className={`sb-col-${c.key}${sorted ? ' sb-th-sorted' : ''}`}
                    aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <span className="sb-th-label">{c.label}</span>
                    {sorted && <span className="sb-sort-mark" aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={columns.length} className="sb-empty">No sentences match these filters.</td></tr>
            )}
            {rows.map((rec, i) => {
              // One open record page at a time, under the row that was clicked (several rows
              // can share a docket or an opinion).
              const key = `${rec.record_id || rec.docket || 'r'}-${offset + i}`;
              const expanded = expandedKey === key;
              return (
                <Fragment key={key}>
                  <tr
                    className={`sb-row${expanded ? ' sb-row-expanded' : ''}${i % 2 ? ' sb-row-alt' : ''}${rec.conviction_reversed ? ' sb-row-reversed' : ''}`}
                    onClick={() => setExpandedKey(expanded ? null : key)}
                  >
                    {columns.map((c) => <td key={c.key} className={`sb-col-${c.key}`}>{renderCell(rec, c.key, expanded)}</td>)}
                  </tr>
                  {Array.isArray(rec.facts_snippets) && rec.facts_snippets.length > 0 && (
                    <tr className="sb-facts-row"><td colSpan={columns.length}><FactSnippets snippets={rec.facts_snippets} /></td></tr>
                  )}
                  {expanded && (
                    <tr className="sb-detail-row">
                      <td colSpan={columns.length}><RecordDetail rec={rec} /></td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="sb-paging">
        <button
          className="btn btn-sm btn-ghost"
          disabled={loading || offset === 0}
          onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
        >
          ← Prev
        </button>
        <span className="sb-page-label">Page {page} of {pages}</span>
        <button
          className="btn btn-sm btn-ghost"
          disabled={loading || total == null || offset + PAGE_SIZE >= total}
          onClick={() => load(offset + PAGE_SIZE)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
